// @ts-nocheck

import {useAuth} from '../../../app/modules/auth'
import {useIntl} from 'react-intl'
import React, {FC, useState, useEffect} from 'react'
import axios, {AxiosResponse} from 'axios'
import {useLocation, useNavigate} from 'react-router-dom'
import './profile.scss'
import PaypalSubscription from '../../components/PaypalSubscription'

const API_URL = process.env.REACT_APP_API_URL

// Card charges carry the amount in TotalRicipient; manual bank-transfer / Bit invoices
// (/admin/payments) carry it in TotalIncludeVAT / TotalCustomeTransaction with TotalRicipient 0.
const invoiceAmount = (invoice: any): number => {
  for (const k of ['TotalRicipient', 'TotalIncludeVAT', 'TotalIncludeVATNIS', 'TotalCustomeTransaction']) {
    const n = parseFloat(String(invoice?.[k] ?? '').replace(/,/g, ''))
    if (!isNaN(n) && n !== 0) return n
  }
  return 0
}

// Refund / credit note (Cardcom document type 2, or a negative amount)
const isRefund = (invoice: any): boolean =>
  Number(invoice?.InvoiceType) === 2 || invoice?.IsNegetive === true || invoiceAmount(invoice) < 0

const paymentMethodLabel = (invoice: any): string => {
  const nz = (k) => Number(invoice?.[k] || 0) !== 0
  if (nz('TotalCreditCard') || nz('TotalCreditCardNIS')) return 'כרטיס אשראי'
  if (nz('TotalChash')) return 'מזומן'
  if (nz('TotalCheques')) return 'צ׳ק'
  if (String(invoice?.internal_invoice_id || '').startsWith('paypal_')) return 'PayPal'
  if (nz('TotalCustomeTransaction')) return 'העברה בנקאית / ביט'
  return 'תשלום'
}

// Charge-time rows carry a made-up timestamp number until the Cardcom sync runs; don't show it
const realInvoiceNumber = (invoice: any) => {
  const n = Number(invoice?.Invoice_Number)
  return n > 0 && n < 1e9 ? n : null
}

const formatMoney = (n: number): string =>
  (n < 0 ? '-' : '') + '₪' + Math.abs(n).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const PaymentInformationWrapper = ({showinvoices}) => {

  const {currentUser, logout, auth} = useAuth();
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();

  // Get tab from URL query parameter
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState(tabParam || 'profile');

  // Handle tab change - update URL
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`/invoices?tab=${tab}`, { replace: true });
  };
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);

  // Billing state
  const [updatingCard, setUpdatingCard] = useState(false);
  const [payingBalance, setPayingBalance] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardIframeUrl, setCardIframeUrl] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  // Update active tab when URL changes
  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Profile edit state
  const [firstName, setFirstName] = useState(currentUser?.first_name || '');
  const [lastName, setLastName] = useState(currentUser?.last_name || '');
  const [companyName, setCompanyName] = useState(currentUser?.company_name || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState((currentUser as any)?.branding_logo_url || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingInvoices(true);
        var data = await axios.get(`${API_URL}/api/get-invoices`);
        // חשבוניות על סכום 0 לא מעניינות את הלקוח - לא מציגים אותן
        const nonZero = (Array.isArray(data.data) ? data.data : []).filter(
          (invoice) => invoiceAmount(invoice) !== 0
        );
        setInvoices(nonZero);
      } catch (e) {
        console.log(e);
      }
      setLoadingInvoices(false);
    })()
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('הסיסמאות אינן תואמות');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }

    try {
      setChangingPassword(true);
      await axios.post(`${API_URL}/api/change-password`, {
        api_token: auth?.api_token,
        current_password: currentPassword,
        new_password: newPassword
      });
      setPasswordSuccess('הסיסמה שונתה בהצלחה');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      setPasswordError('שגיאה בשינוי הסיסמה. אנא בדוק את הסיסמה הנוכחית');
    }
    setChangingPassword(false);
  };

  // Update credit card
  const handleUpdateCard = async () => {
    try {
      setUpdatingCard(true);
      const response = await axios.get(`${API_URL}/api/generate-token`);
      if (response.data.url) {
        setCardIframeUrl(response.data.url);
        setShowCardModal(true);
      }
    } catch (e) {
      console.error('Error generating token URL:', e);
    }
    setUpdatingCard(false);
  };

  // Pay the outstanding balance now: charges monthly_payment_amount, saves
  // the token, and extends the billing period (same flow as updating a card).
  const handlePayBalance = async () => {
    try {
      setPayingBalance(true);
      const response = await axios.get(`${API_URL}/api/generate-payment-token`);
      if (response.data.url) {
        setCardIframeUrl(response.data.url);
        setShowCardModal(true);
      }
    } catch (e) {
      console.error('Error generating payment URL:', e);
    }
    setPayingBalance(false);
  };

  // Close card modal and refresh if needed
  const handleCloseCardModal = () => {
    setShowCardModal(false);
    setCardIframeUrl('');
  };

  // Cancel subscription
  const handleCancelSubscription = async () => {
    try {
      setCancellingSubscription(true);
      await axios.post(`${API_URL}/api/request-cancellation`, {
        api_token: auth?.api_token,
        reason: cancelReason
      });
      setCancelSuccess(true);
      setShowCancelModal(false);
      setCancelReason('');
    } catch (e) {
      console.error('Error requesting cancellation:', e);
    }
    setCancellingSubscription(false);
  };

  // Calculate days remaining in billing period
  const getDaysRemaining = () => {
    if (!currentUser?.trial_period_end) return 0;
    const endDate = new Date(currentUser.trial_period_end);
    const now = new Date();
    const diff = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setProfileSuccess('');
      await axios.post(`${API_URL}/api/update-profile`, {
        api_token: auth?.api_token,
        first_name: firstName,
        last_name: lastName,
        company_name: companyName
      });
      setProfileSuccess('הפרטים נשמרו בהצלחה');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (e) {
      console.log(e);
    }
    setSavingProfile(false);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('יש להעלות קובץ תמונה בלבד');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('גודל הקובץ חייב להיות עד 5MB');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('api_token', auth?.api_token || '');
      const res = await axios.post(`${API_URL}/api/upload-branding-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.logo_url) {
        setBrandingLogoUrl(res.data.logo_url);
        setProfileSuccess('הלוגו הועלה בהצלחה! הלוגו יופיע על תמונות ופוסטים חדשים');
        setTimeout(() => setProfileSuccess(''), 5000);
      }
    } catch (err) {
      console.error('Logo upload error:', err);
      alert('שגיאה בהעלאת הלוגו');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.post(`${API_URL}/api/update-profile`, {
        api_token: auth?.api_token,
        branding_logo_url: ''
      });
      setBrandingLogoUrl('');
      setProfileSuccess('הלוגו הוסר');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (err) {
      console.error('Remove logo error:', err);
    }
  };

  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          {currentUser?.first_name?.charAt(0) || currentUser?.email?.charAt(0) || '?'}
        </div>
        <div className="profile-info">
          <h1 className="profile-name">
            {currentUser?.first_name} {currentUser?.last_name}
          </h1>
          <p className="profile-email">{currentUser?.email}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button
          className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => handleTabChange('profile')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="tab-icon">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
          </svg>
          פרטי חשבון
        </button>
        <button
          className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => handleTabChange('password')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="tab-icon">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
          </svg>
          שינוי סיסמה
        </button>
        <button
          className={`tab-btn ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => handleTabChange('invoices')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="tab-icon">
            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
          </svg>
          חשבוניות
        </button>
        <button
          className={`tab-btn ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => handleTabChange('billing')}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="tab-icon">
            <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
          </svg>
          תשלום ומנוי
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="content-card">
            <h2 className="card-title">פרטי חשבון</h2>

            {profileSuccess && (
              <div className="alert alert-success">
                <svg viewBox="0 0 24 24" fill="currentColor" className="alert-icon">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                {profileSuccess}
              </div>
            )}

            {/* Editable Profile Fields */}
            <div className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">שם פרטי</label>
                  <input
                    type="text"
                    className="form-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="שם פרטי"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">שם משפחה</label>
                  <input
                    type="text"
                    className="form-input"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="שם משפחה"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">שם החברה (לחשבוניות)</label>
                <input
                  type="text"
                  className="form-input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="הזן שם חברה..."
                />
              </div>

              <button
                type="button"
                className="submit-btn"
                onClick={handleSaveProfile}
                disabled={savingProfile}
              >
                {savingProfile ? (
                  <>
                    <span className="btn-spinner"></span>
                    שומר...
                  </>
                ) : 'שמור שינויים'}
              </button>
            </div>

            {/* Branding Logo */}
            <div style={{marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e4e6ef'}}>
              <h3 style={{fontSize: '16px', fontWeight: 600, marginBottom: '8px'}}>לוגו מיתוג</h3>
              <p style={{fontSize: '13px', color: '#7e8299', marginBottom: '16px'}}>
                הלוגו יוצג בשקיפות בפינה הימנית התחתונה של כל תמונה וסרטון שמפורסמים בפוסטים שלך
              </p>

              {brandingLogoUrl ? (
                <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                  {/* Preview with example */}
                  <div style={{position: 'relative', width: '200px', height: '150px', background: '#ddd', borderRadius: '8px', overflow: 'hidden'}}>
                    <div style={{width: '100%', height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 500}}>
                      תצוגה מקדימה
                    </div>
                    <img
                      src={brandingLogoUrl}
                      alt="Logo preview"
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        maxWidth: '60px',
                        maxHeight: '40px',
                        opacity: 0.5,
                        objectFit: 'contain',
                      }}
                    />
                  </div>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    <img src={brandingLogoUrl} alt="Current logo" style={{maxWidth: '120px', maxHeight: '60px', objectFit: 'contain', border: '1px solid #e4e6ef', borderRadius: '6px', padding: '4px'}} />
                    <div style={{display: 'flex', gap: '8px'}}>
                      <label style={{
                        padding: '6px 12px', background: '#3699ff', color: '#fff', borderRadius: '6px',
                        fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                      }}>
                        {uploadingLogo ? 'מעלה...' : 'החלף לוגו'}
                        <input type="file" accept="image/*" onChange={handleLogoUpload} style={{display: 'none'}} disabled={uploadingLogo} />
                      </label>
                      <button onClick={handleRemoveLogo} style={{
                        padding: '6px 12px', background: '#fff', color: '#f64e60', border: '1px solid #f64e60',
                        borderRadius: '6px', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
                      }}>
                        הסר לוגו
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <label style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                  padding: '24px', border: '2px dashed #c4c4c4', borderRadius: '8px', cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}>
                  <span style={{fontSize: '32px'}}>🖼️</span>
                  <span style={{fontSize: '14px', fontWeight: 500, color: '#5e6278'}}>
                    {uploadingLogo ? 'מעלה...' : 'לחץ להעלאת לוגו'}
                  </span>
                  <span style={{fontSize: '12px', color: '#a1a5b7'}}>PNG, JPG, SVG — עד 5MB</span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} style={{display: 'none'}} disabled={uploadingLogo} />
                </label>
              )}
            </div>

            {/* Read-only Details */}
            <div className="profile-details">
              <div className="detail-row">
                <span className="detail-label">אימייל</span>
                <span className="detail-value">{currentUser?.email || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">טלפון</span>
                <span className="detail-value">{currentUser?.phone || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">תאריך הצטרפות</span>
                <span className="detail-value">
                  {currentUser?.created_at ? new Date(currentUser.created_at).toLocaleDateString('he-IL') : '-'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Password Tab */}
        {activeTab === 'password' && (
          <div className="content-card">
            <h2 className="card-title">שינוי סיסמה</h2>
            <form onSubmit={handleChangePassword} className="password-form">
              {passwordError && (
                <div className="alert alert-error">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="alert-icon">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="alert alert-success">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="alert-icon">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  {passwordSuccess}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">סיסמה נוכחית <span className="optional-label">(אופציונלי)</span></label>
                <input
                  type="password"
                  className="form-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="השאר ריק אם לא זוכר"
                />
              </div>
              <div className="form-group">
                <label className="form-label">סיסמה חדשה</label>
                <input
                  type="password"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">אימות סיסמה חדשה</label>
                <input
                  type="password"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <button
                type="submit"
                className="submit-btn"
                disabled={changingPassword}
              >
                {changingPassword ? (
                  <>
                    <span className="btn-spinner"></span>
                    מעדכן...
                  </>
                ) : 'שנה סיסמה'}
              </button>
            </form>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="content-card">
            <h2 className="card-title">החשבוניות שלי</h2>

            {loadingInvoices && (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <span>טוען חשבוניות...</span>
              </div>
            )}

            {!loadingInvoices && invoices.length === 0 && (
              <div className="empty-state">
                <svg viewBox="0 0 24 24" fill="currentColor" className="empty-icon">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/>
                </svg>
                <p>אין חשבוניות להצגה</p>
              </div>
            )}

            {!loadingInvoices && invoices.length > 0 && (() => {
              const rows = [...invoices].sort((a, b) => new Date(b.InvoiceDate).getTime() - new Date(a.InvoiceDate).getTime());
              const totalPaid = rows.reduce((sum, inv) => sum + invoiceAmount(inv), 0);
              const paymentsCount = rows.filter((inv) => !isRefund(inv)).length;
              let lastYear = null;
              return (
                <>
                  <div className="invoice-summary">
                    <div className="summary-item">
                      <span className="summary-label">סה״כ שולם</span>
                      <span className="summary-value">{formatMoney(totalPaid)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">מספר תשלומים</span>
                      <span className="summary-value">{paymentsCount}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">תשלום אחרון</span>
                      <span className="summary-value">{formatDate(rows[0].InvoiceDate)}</span>
                    </div>
                  </div>

                  <div className="invoices-list">
                    {rows.map((invoice, index) => {
                      const refund = isRefund(invoice);
                      const d = new Date(invoice.InvoiceDate);
                      const year = d.getFullYear();
                      const showYear = year !== lastYear;
                      lastYear = year;
                      const docNumber = realInvoiceNumber(invoice);
                      return (
                        <React.Fragment key={invoice._id || index}>
                          {showYear && <div className="invoice-year">{year}</div>}
                          <div className={`invoice-row ${refund ? 'is-refund' : ''}`}>
                            <div className="invoice-icon">
                              {refund ? (
                                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/></svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                              )}
                            </div>
                            <div className="invoice-info">
                              <div className="invoice-title">
                                {refund ? 'זיכוי' : 'תשלום'} · {d.toLocaleDateString('he-IL', { month: 'long', year: 'numeric' })}
                              </div>
                              <div className="invoice-meta">
                                <span>{formatDate(invoice.InvoiceDate)}</span>
                                <span>{paymentMethodLabel(invoice)}</span>
                                {docNumber && <span>{refund ? 'חשבונית זיכוי' : 'חשבונית'} מס׳ {docNumber}</span>}
                              </div>
                            </div>
                            <div className="invoice-amount">{formatMoney(invoiceAmount(invoice))}</div>
                            {invoice.DocUrl ? (
                              <a
                                href={invoice.DocUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="download-btn"
                                title="הורדת החשבונית (PDF)"
                              >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                </svg>
                                הורדה
                              </a>
                            ) : (
                              <span className="download-btn disabled">בהכנה</span>
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="content-card">
            <h2 className="card-title">תשלום ומנוי</h2>

            {cancelSuccess && (
              <div className="alert alert-success" style={{marginBottom: '20px'}}>
                <svg viewBox="0 0 24 24" fill="currentColor" className="alert-icon">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                בקשת הביטול נשלחה בהצלחה. ניצור איתך קשר בהקדם.
              </div>
            )}

            {/* Subscription Status */}
            <div className="billing-section">
              <h3 className="section-title">סטטוס מנוי</h3>
              <div className="billing-card">
                <div className="billing-row">
                  <span className="billing-label">סוג מנוי</span>
                  <span className="billing-value">
                    <span className="plan-badge">מנוי פעיל</span>
                  </span>
                </div>
                <div className="billing-row">
                  <span className="billing-label">תשלום חודשי</span>
                  <span className="billing-value price">₪{currentUser?.monthly_payment_amount || 118}</span>
                </div>
                <div className="billing-row">
                  <span className="billing-label">תאריך חידוש</span>
                  <span className="billing-value">{formatDate(currentUser?.trial_period_end)}</span>
                </div>
                <div className="billing-row">
                  <span className="billing-label">ימים עד לחידוש</span>
                  <span className="billing-value">{getDaysRemaining()} ימים</span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="billing-section">
              <h3 className="section-title">אמצעי תשלום</h3>
              <div className="billing-card">
                <div className="payment-method">
                  <div className="card-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                    </svg>
                  </div>
                  <div className="card-details">
                    {currentUser?.payment_tokens > 0 ? (
                      <>
                        <span className="card-type">{currentUser?.card_brand || 'כרטיס אשראי'}</span>
                        <span className="card-number">•••• •••• •••• {currentUser?.card_last4 || '****'}</span>
                      </>
                    ) : (
                      <span className="no-card">לא הוגדר אמצעי תשלום</span>
                    )}
                  </div>
                  <button
                    className="update-card-btn"
                    onClick={handleUpdateCard}
                    disabled={updatingCard}
                  >
                    {updatingCard ? (
                      <>
                        <span className="btn-spinner"></span>
                        טוען...
                      </>
                    ) : currentUser?.payment_tokens > 0 ? 'עדכן כרטיס' : 'הוסף כרטיס'}
                  </button>
                  {(currentUser?.monthly_payment_amount || 0) > 0 && (
                    <button
                      className="update-card-btn"
                      onClick={handlePayBalance}
                      disabled={payingBalance}
                      style={{marginInlineStart: '8px', background: '#50cd89'}}
                    >
                      {payingBalance ? (
                        <>
                          <span className="btn-spinner"></span>
                          טוען...
                        </>
                      ) : `שלם יתרה ₪${currentUser?.monthly_payment_amount}`}
                    </button>
                  )}
                </div>
                {/* Updating the card only stores it (Cardcom shows ₪0) — it used to
                    also charge the monthly amount when the renewal date was close. */}
                <p style={{margin: '10px 2px 0', fontSize: '12.5px', color: '#7e8299', lineHeight: 1.5}}>
                  עדכון או הוספת כרטיס שומרים את פרטי האשראי בלבד — לא מתבצע חיוב.
                  {(currentUser?.monthly_payment_amount || 0) > 0 ? ' לתשלום בפועל השתמשו בכפתור "שלם יתרה".' : ''}
                </p>
                {searchParams.get('paypal') === 'success' && (
                  <div style={{marginTop: 12, padding: '10px 12px', borderRadius: 8, background: '#e8fff3', color: '#1b8a52', fontSize: 13.5}}>
                    מנוי ה-PayPal אושר. החיוב והחשבונית יופיעו כאן תוך כמה דקות.
                  </div>
                )}
                {searchParams.get('paypal') === 'pending' && (
                  <div style={{marginTop: 12, padding: '10px 12px', borderRadius: 8, background: '#fff8dd', color: '#8a6d00', fontSize: 13.5}}>
                    המנוי ב-PayPal בתהליך אישור. רעננו את הדף בעוד כמה דקות.
                  </div>
                )}
                <PaypalSubscription />
              </div>
            </div>

            {/* Cancel Subscription */}
            <div className="billing-section">
              <h3 className="section-title">ביטול מנוי</h3>
              <div className="billing-card cancel-section">
                <p className="cancel-text">
                  ביטול המנוי יכנס לתוקף בתום תקופת החיוב הנוכחית.
                  לאחר הביטול לא תחויב יותר והגישה למערכת תופסק.
                </p>
                <button
                  className="cancel-btn"
                  onClick={() => setShowCancelModal(true)}
                >
                  בקש ביטול מנוי
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h3>ביטול מנוי</h3>
              <button className="modal-close" onClick={() => setShowCancelModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>אנחנו מצטערים לשמוע שאתה רוצה לעזוב 😢</p>
              <p>נשמח לדעת מה הסיבה לביטול כדי שנוכל להשתפר:</p>
              <textarea
                className="cancel-reason-input"
                placeholder="ספר לנו למה אתה מבטל... (אופציונלי)"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button
                className="modal-btn secondary"
                onClick={() => setShowCancelModal(false)}
              >
                חזרה
              </button>
              <button
                className="modal-btn danger"
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
              >
                {cancellingSubscription ? 'שולח בקשה...' : 'אשר ביטול'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Card Modal */}
      {showCardModal && (
        <div className="modal-backdrop">
          <div className="modal-content card-modal">
            <div className="modal-header">
              <h3>עדכון פרטי כרטיס אשראי</h3>
              <button className="modal-close" onClick={handleCloseCardModal}>×</button>
            </div>
            <div className="modal-body card-iframe-container">
              {cardIframeUrl && (
                <iframe
                  src={cardIframeUrl}
                  title="עדכון כרטיס אשראי"
                  className="card-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export {PaymentInformationWrapper}
